"use client"

import { useRouter } from 'next/navigation';
import { getUser, updateAdvertisement } from '@/app/actions'; // Import the updateAdvertisement function
import { useState } from 'react';
import { Trash, Share, Lightning, EyeSlash, Clock } from '@phosphor-icons/react/dist/ssr';
import { RecordModel } from 'pocketbase';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';

const AdvertActions = ({ advert }: { advert: any }) => {
  const router = useRouter();
  const [isPublishing, setIsPublishing] = useState(false);
  const [isUnpublishing, setIsUnpublishing] = useState(false);
  const { data: currentUser } = useQuery({
    queryKey: ["user"],
    queryFn: getUser,
    staleTime: Infinity,
  });

  const handleEdit = () => {
    const path = `/classifieds/new?adId=${advert.id}&step=8`;
    router.push(path);
  };

  const handlePublish = async () => {
    setIsPublishing(true);
    try {
      const published: any = await updateAdvertisement(advert.id, { status: 'active' });
      // updateAdvertisement resolves with an empty result object instead of
      // throwing when the update fails, so check for a real record.
      if (!published?.id) {
        throw new Error('Advertisement was not published');
      }
      // A handful of records carry an empty slug; fall back to the list rather
      // than pushing a slugless confirmation URL that would 404.
      const slug = published.slug || advert.slug;
      router.push(
        slug ? `/account/my-adverts/published/${slug}` : '/account/my-adverts'
      );
    } catch (error) {
      console.error('Failed to publish advertisement:', error);
      toast.error('Failed to publish your advert. Please try again.');
      setIsPublishing(false);
    }
  };

  const handleUnpublish = async () => {
    setIsUnpublishing(true);
    try {
      const updated: any = await updateAdvertisement(advert.id, { status: 'draft' });
      // updateAdvertisement resolves with an empty result object instead of
      // throwing when the update fails, so check for a real record.
      if (!updated?.id) {
        throw new Error('Advertisement was not unpublished');
      }
      router.refresh();
    } catch (error) {
      console.error('Failed to unpublish advertisement:', error);
      toast.error('Failed to unpublish your advert. Please try again.');
      setIsUnpublishing(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm("Are you sure you want to delete this advertisement?")) return;
    try {
      // Update the advertisement status to 'deleted'
      const newStatus = advert.status === 'draft' ? 'deleted-draft' : 'deleted';
      await updateAdvertisement(advert.id, { status: newStatus });
      router.refresh();
    } catch (error) {
      console.error("Failed to delete advertisement:", error);
    }
  };

  const handleBoost = async () => {
    const path = `/classifieds/new?adId=${advert.id}&step=7`;
    router.push(path);
  };

  return (advert.user.id === (currentUser as unknown as RecordModel).id) ? (
    <div
      className="flex gap-4 flex-wrap"
      // The whole card is a link to the published advert; keep action button
      // clicks from bubbling up and navigating away.
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      {/* 1. Boost — green solid. Hidden while a boost is already running. */}
      {!advert.boosted && (
        <button
          className="bg-primary text-white px-4 py-2 rounded-full flex items-center hover:bg-primary-focus transition-colors"
          onClick={handleBoost}
        >
          <Lightning weight="fill" className="mr-2" />
          Boost
        </button>
      )}
      {/* 2. Edit — blue outline */}
      <button
        className="border-2 border-secondary text-secondary bg-transparent px-4 py-2 rounded-full flex items-center hover:bg-secondary hover:text-white transition-colors"
        onClick={handleEdit}
      >
        <svg
          className="w-4 h-4 mr-2"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
          />
        </svg>
        Edit
      </button>
      {/* 3. Unpublish (or Publish for drafts) — blue outline.
          A pending advert is already in the review queue, so it offers neither:
          publishing again would just re-queue it. */}
      {advert.status === 'pending' ? (
        <span
          className="border-2 border-base-content/20 text-base-content/60 bg-transparent px-4 py-2 rounded-full flex flex-row items-center cursor-default"
          title="A reviewer is checking this advert before it goes live"
        >
          <Clock size={18} weight="bold" className="mr-2" />
          Awaiting review
        </span>
      ) : advert.status !== 'active' ? (
        <button
          className="border-2 border-secondary text-secondary bg-transparent px-4 py-2 rounded-full flex flex-row items-center hover:bg-secondary hover:text-white transition-colors disabled:opacity-60"
          onClick={handlePublish}
          disabled={isPublishing}
        >
          <Share size={18} weight="bold" className="mr-2" />
          {isPublishing ? 'Publishing...' : 'Publish'}
        </button>
      ) : (
        <button
          className="border-2 border-secondary text-secondary bg-transparent px-4 py-2 rounded-full flex flex-row items-center hover:bg-secondary hover:text-white transition-colors disabled:opacity-60"
          onClick={handleUnpublish}
          disabled={isUnpublishing}
        >
          <EyeSlash size={18} weight="bold" className="mr-2" />
          {isUnpublishing ? 'Unpublishing...' : 'Unpublish'}
        </button>
      )}
      {/* 4. Delete — red outline */}
      <button
        className="border-2 border-accent text-accent bg-transparent px-4 py-2 rounded-full flex items-center hover:bg-accent hover:text-white transition-colors"
        onClick={handleDelete}
      >
        <Trash weight="fill" className="mr-2" />
        Delete
      </button>
    </div>
  ) : (<></>);
};

export default AdvertActions;